import { AccountCreateModal } from '@account'
import { CurrencyBadge, CurrencyIcon, CustomPreloader, MMButton } from '@components'
import { Plus } from '@icons'
import cn from '@integrations/classnames'
import toast from '@integrations/notifications'
import type { SyntheticEvent } from '@integrations/react'
import { useContext } from '@integrations/react'
import { extractRecurringIbanFees, useCurrencies } from 'mmfintech-backend-api'
import type { AccountBalanceResponse, BankAccountFeeResponse, BankAccountScheme } from 'mmfintech-backend-api/types'
import { formatMoney, GlobalContext, isValidArray, tr } from 'mmfintech-commons'

import styles from './IbanRequest.module.scss'

// Plain white check that inherits the enclosing circle's `color` (the shared @icons checkmarks bake
// in their own green circle, which reads as an ugly nested badge inside our blue/green circles).
function CheckIcon() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth={3}
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <polyline points='20 6 9 17 4 12' />
    </svg>
  )
}

function networkSubtitle(scheme: string): string {
  switch (scheme) {
    case 'SEPA':
      return tr('FRONTEND.IBAN_REQUEST.NETWORK.SEPA_SUB', 'Eurozone, fast & low cost')
    case 'SWIFT':
      return tr('FRONTEND.IBAN_REQUEST.NETWORK.SWIFT_SUB', 'Global, multi-currency')
    case 'LOCAL':
      return tr('FRONTEND.IBAN_REQUEST.NETWORK.LOCAL_SUB', 'Local clearing')
    default:
      return ''
  }
}

interface Props {
  availableSchemes: BankAccountScheme[]
  selectedScheme: BankAccountScheme
  setSelectedScheme: (scheme: BankAccountScheme) => void
  ibanSchemeCurrencies: Record<string, string[]>
  ibanSchemeCurrenciesFetching: boolean
  selectedCurrency: string
  setSelectedCurrency: (currency: string) => void
  accounts: AccountBalanceResponse[]
  accountsFetching: boolean
  selectedAccount: AccountBalanceResponse
  setSelectedAccount: (account: AccountBalanceResponse) => void
  ibanIssuingFee?: BankAccountFeeResponse
  fees?: BankAccountFeeResponse[]
  feePaid: boolean
  showFee: boolean
  onReloadAccounts: (accountId?: number) => void
  onContinue: () => void
  onCancel: () => void
}

function SetupStep({
  availableSchemes,
  selectedScheme,
  setSelectedScheme,
  ibanSchemeCurrencies,
  ibanSchemeCurrenciesFetching,
  selectedCurrency,
  setSelectedCurrency,
  accounts,
  accountsFetching,
  selectedAccount,
  setSelectedAccount,
  ibanIssuingFee,
  fees,
  feePaid,
  showFee,
  onReloadAccounts,
  onContinue,
  onCancel
}: Props) {
  const { modalShow } = useContext(GlobalContext)
  const { getCurrencyPrecision } = useCurrencies()

  const currencyOptions = selectedScheme ? (ibanSchemeCurrencies?.[selectedScheme] ?? []) : []

  const networkComplete = Boolean(selectedScheme)
  const currencyComplete = Boolean(selectedCurrency)
  const accountComplete = Boolean(selectedAccount)
  const canContinue = networkComplete && currencyComplete && accountComplete

  const recurringFees = extractRecurringIbanFees(fees)

  function openCreateAccountModal() {
    if (!selectedCurrency) return
    modalShow({
      options: { closeOnClickOutside: false, size: 'auto' },
      content: (
        <AccountCreateModal
          filteredCurrencies={[selectedCurrency]}
          onSuccess={(currencyCode: string, accountId: number) => {
            onReloadAccounts(accountId)
            toast.remove()
            toast.success(
              tr('FRONTEND.ACCOUNTS.SUCCESS_MESSAGE', '{{CURRENCY}} account successfully added', {
                CURRENCY: currencyCode
              })
            )
          }}
        />
      )
    })
  }

  return (
    <div className={cn(styles['iban-step-view'])}>
      {/* ── Section 1: Payment network ── */}
      <div
        className={cn(styles['iban-setup-section'], {
          [styles['complete']]: networkComplete,
          [styles['active']]: !networkComplete
        })}
      >
        <div className={cn(styles['iban-section-header'])}>
          <div className={cn(styles['iban-section-title'])}>
            <span className={cn(styles['iban-step-marker'], 'iban-step-marker', styles['iban-section-badge'])}>
              {networkComplete ? <CheckIcon /> : 1}
            </span>
            {tr('FRONTEND.IBAN_REQUEST.NETWORK_TITLE', 'Choose a payment network for your IBAN')}
          </div>
        </div>
        <div className={cn(styles['iban-network-row'])}>
          {ibanSchemeCurrenciesFetching ? (
            <CustomPreloader />
          ) : (
            isValidArray(availableSchemes) &&
            availableSchemes.map((scheme) => (
              <div
                key={scheme}
                className={cn(styles['iban-choice-card'], 'iban-choice-card', styles['iban-net-card'], {
                  [styles['selected']]: selectedScheme === scheme
                })}
                onClick={() => setSelectedScheme(scheme)}
                data-test={`iban-network-${String(scheme).toLowerCase()}`}
              >
                <span className={cn(styles['iban-net-icon'], styles[String(scheme).toLowerCase()])}>{scheme}</span>
                <div className={cn(styles['iban-choice-info'], 'iban-choice-info')}>
                  <div className={cn(styles['iban-net-title'])}>{tr(`METADATA.PAYMENT_OPTIONS.${scheme}`, scheme)}</div>
                  <div className={cn(styles['iban-choice-meta'], 'iban-choice-meta', styles['iban-net-sub'])}>
                    {networkSubtitle(scheme)}
                  </div>
                </div>
                {selectedScheme === scheme && (
                  <span className={cn(styles['iban-choice-check'], 'iban-choice-check')}>
                    <CheckIcon />
                  </span>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Section 2: Currency ── */}
      <div
        className={cn(styles['iban-setup-section'], {
          [styles['disabled']]: !networkComplete,
          [styles['complete']]: currencyComplete,
          [styles['active']]: networkComplete && !currencyComplete
        })}
      >
        <div className={cn(styles['iban-section-header'])}>
          <div className={cn(styles['iban-section-title'])}>
            <span className={cn(styles['iban-step-marker'], 'iban-step-marker', styles['iban-section-badge'])}>
              {currencyComplete ? <CheckIcon /> : 2}
            </span>
            {tr('FRONTEND.IBAN_REQUEST.CURRENCY_TITLE', 'Choose a currency')}
          </div>
        </div>
        <div className={cn(styles['iban-ccy-grid'])}>
          {currencyOptions.map((code) => (
            <CurrencyBadge
              key={code}
              currencyCode={code}
              isSelected={selectedCurrency === code}
              onClick={(e: SyntheticEvent) => {
                e.preventDefault()
                setSelectedCurrency(code)
              }}
            />
          ))}
        </div>
      </div>

      {/* ── Section 3: Linked account ── */}
      <div
        className={cn(styles['iban-setup-section'], {
          [styles['disabled']]: !currencyComplete,
          [styles['complete']]: accountComplete,
          [styles['active']]: currencyComplete && !accountComplete
        })}
      >
        <div className={cn(styles['iban-section-header'])}>
          <div className={cn(styles['iban-section-title'])}>
            <span className={cn(styles['iban-step-marker'], 'iban-step-marker', styles['iban-section-badge'])}>
              {accountComplete ? <CheckIcon /> : 3}
            </span>
            {tr('FRONTEND.IBAN_REQUEST.ACCOUNT_TITLE', 'Choose a currency account to associate with the IBAN')}
          </div>
        </div>

        {accountsFetching ? (
          <CustomPreloader />
        ) : isValidArray(accounts) ? (
          <div className={cn(styles['iban-acct-list'])}>
            {accounts.map((account) => (
              <div
                key={account.id}
                className={cn(styles['iban-choice-card'], 'iban-choice-card', styles['iban-acct-card'], {
                  [styles['selected']]: selectedAccount?.id === account.id
                })}
                onClick={() => setSelectedAccount(account)}
                data-test={`iban-account-${account.id}`}
              >
                <CurrencyIcon currency={account.currencyCode} size='28px' circle />
                <div className={cn(styles['iban-choice-info'], 'iban-choice-info')}>
                  <div className={cn(styles['iban-acct-name'])}>{account.name}</div>
                  <div className={cn(styles['iban-choice-meta'], 'iban-choice-meta')}>
                    {tr('FRONTEND.COMPONENTS.SELECT_ACCOUNT.BALANCE_LABEL', 'Balance')}:{' '}
                    {formatMoney(account.balance, account.currencyCode)}
                  </div>
                </div>
                {selectedAccount?.id === account.id && (
                  <span className={cn(styles['iban-choice-check'], 'iban-choice-check')}>
                    <CheckIcon />
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          currencyComplete && (
            <div className={cn(styles['iban-acct-empty'])}>
              {tr(
                'FRONTEND.IBAN_REQUEST.NO_ACCOUNTS',
                "You don't have a {{CURRENCY}} account yet. Create one to continue.",
                {
                  CURRENCY: selectedCurrency
                }
              )}
            </div>
          )
        )}

        {currencyComplete && (
          <MMButton
            type='button'
            variant='secondary'
            leftIcon={<Plus />}
            caption={tr('FRONTEND.IBAN_REQUEST.CREATE_ACCOUNT', 'Create new {{CURRENCY}} account', {
              CURRENCY: selectedCurrency
            })}
            onClick={openCreateAccountModal}
            className={cn(styles['iban-acct-create'], 'iban-acct-create')}
            data-test='iban-create-account'
          />
        )}
      </div>

      {/* ── Fee strip ── */}
      {showFee && (
        <div className={cn(styles['iban-fee-strip'])}>
          <div className={cn(styles['iban-fee-strip-note'])}>
            {tr(
              'FRONTEND.IBAN_REQUEST.FEE_DISCLAIMER',
              'Please note that the IBAN fee will be deducted from your account. You may need to update your existing or provide extra documents. Contact your customer support manager for more information regarding your request.'
            )}
          </div>
          <div className={cn(styles['iban-fee-strip-values'])}>
            <div className={cn(styles['iban-fee-strip-row'])}>
              <span className={cn(styles['iban-fee-strip-label'])}>
                {tr('FRONTEND.IBAN_REQUEST.ISSUING_FEE', 'Issuing fee:')}
              </span>
              <span className={cn(styles['iban-fee-strip-val'], { [styles['free']]: feePaid })}>
                {feePaid
                  ? tr('FRONTEND.IBAN_REQUEST.FREE_ALREADY_PAID', 'Free (already paid)')
                  : ibanIssuingFee?.value != null
                    ? formatMoney(
                        ibanIssuingFee.value,
                        ibanIssuingFee.feeCurrency,
                        getCurrencyPrecision(ibanIssuingFee.feeCurrency)
                      )
                    : '—'}
              </span>
            </div>
            {isValidArray(recurringFees) &&
              recurringFees.map((fee) => (
                <div className={cn(styles['iban-fee-strip-row'])} key={fee.feeType}>
                  <span className={cn(styles['iban-fee-strip-label'])}>
                    {tr('FRONTEND.IBAN_REQUEST.MAINTENANCE_FEE', 'Monthly maintenance fee')}:
                  </span>
                  <span className={cn(styles['iban-fee-strip-val'])}>
                    {formatMoney(fee.value, fee.feeCurrency, getCurrencyPrecision(fee.feeCurrency))}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className={cn(styles['iban-btn-row'])}>
        <MMButton
          type='button'
          variant='secondary'
          caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
          onClick={onCancel}
          data-test='iban-cancel'
        />
        <MMButton
          type='button'
          variant='primary'
          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
          onClick={onContinue}
          disabled={!canContinue}
          data-test='iban-continue'
        />
      </div>
    </div>
  )
}

export default SetupStep
